import React, { useState } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { FiCheckCircle, FiArrowUpRight } from "react-icons/fi";
import Footer from "./Footer";
import styled from "styled-components";

// Styled components
const HeroSection = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 4rem 0;
  margin-bottom: 3rem;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const CategoryCard = styled(Card)`
  border: none;
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 6px 15px rgba(0,0,0,0.1);
  cursor: pointer;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 20px rgba(0,0,0,0.15);
  }
  
  &.selected {
    border: 3px solid #3498db;
  }
`;

const CardImageContainer = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${CategoryCard}:hover & {
    transform: scale(1.05);
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #3498db;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
`;

const DetailBlock = styled.div`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
  }
`;

const StyledList = styled.ul`
  list-style: none;
  padding-left: 0;
  
  li {
    padding: 0.5rem 0;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: flex-start;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .bullet-icon {
    color: #3498db;
    margin-right: 10px;
    font-size: 1.2rem;
  }
`;

const SectionImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
`;

const categories = [
  {
    name: "Civil",
    image: "/t&d/civil/1.jpeg",
    sections: [
      {
        title: "Civil Engineering",
        content: "In today's construction industry, there's enormous demand for skilled civil engineers who can bridge the gap between academic knowledge and practical site execution.",
        image: "/t&d/civil/2.jpeg",
      },
      {
        title: "What the Institution Provides",
        list: [
          "Engineering Mathematics fundamentals",
          "Comprehensive Surveying techniques",
          "Advanced Structural Analysis",
          "Construction Materials and Methods",
          "Transportation Engineering principles",
          "Project Management frameworks",
          "CAD with theoretical foundations & practical applications",
          "Water Resource Engineering concepts",
          "Geotechnical Engineering basics"
        ],
        image: "/t&d/provide.jpeg",
      },
      {
        title: "What Industry Wants",
        list: [
          "Ability to interpret complex construction drawings and execute on-site",
          "Skills to monitor work progress while ensuring quality & safety standards",
          "Competence in conducting accurate site quantity surveys",
          "Capability to efficiently handle resources & material logistics"
        ],
        image: "/t&d/wants.jpeg",
      },
      {
        title: "What I3D Provides",
        list: [
          "Comprehensive Drawing Reading & Interpretation",
          "Precise Quantification of Concrete/Steel/Finishing materials",
          "Calculation methodologies for Raw Materials",
          "Implementation of Latest Execution Technologies",
          "Professional Project Planning & Scheduling",
          "Advanced Costing & Digitalization techniques",
          "Rigorous Safety & Quality protocols"
        ],
        image: "/jpe/civil_section4.jpg",
      },
      {
        title: "I3D Training Methodology",
        list: [
          "Practical Site-Oriented Training from Day One",
          "Instruction by Seasoned Industry Experts",
          "Emphasis on Drawing Reading + Execution + Billing integration"
        ],
        image: "/jpe/civil_section5.jpg",
      }
    ]
  },
  {
    name: "Electrical",
    image: "/t&d/electrical/1.jpeg",
    sections: [
      {
        title: "Electrical Engineering",
        content: "Our electrical engineering program provides hands-on training covering power systems, industrial wiring, and modern safety standards to create job-ready professionals.",
        image: "/t&d/electrical/2.jpeg",
      },
      {
        title: "What the Institution Provides",
        list: [
          "Core Engineering Mathematics",
          "Network Theory and Analysis",
          "Control System Design",
          "Power Electronics fundamentals",
          "Analog and Digital Electronics",
          "High Voltage Engineering principles",
          "Electromagnetic Field Theory",
          "Signals and Systems analysis",
          "Electrical Machine operations"
        ],
        image: "/t&d/provide.jpeg",
      },
      {
        title: "What I3D Provides",
        list: [
          "Complete Cable Laying Process training",
          "Cable Tray Erection based on Duct Design",
          "Cable Schedule preparation and interpretation",
          "Professional Cable Termination techniques",
          "Insulation Resistance (IR) Testing methods",
          "Earth Resistance Testing procedures",
          "Electrical Panel Erection methodologies",
          "Electrical Switch Gear operations",
          "Transformer Construction principles",
          "Various Motor Types & Starter configurations",
          "Plant Lighting systems"
        ],
        image: "/jpe/electrical_section3.jpg",
      },
      {
        title: "What Industry Wants",
        list: [
          "Manufacturing process knowledge",
          "Installation Testing & Commissioning skills",
          "Electrical Maintenance expertise",
          "Electrical Panel Design capability",
          "Wire, Power, Cable Length calculations",
          "Equipment Protection understanding",
          "Cabling and Installation proficiency",
          "Safety & Quality compliance"
        ],
        image: "/t&d/wants.jpeg"
      }
    ]
  },
  {
    name: "Mechanical",
    image: "/t&d/mechanical/2.jpeg",
    sections: [
      {
        title: "Mechanical Engineering",
        content: "Our mechanical engineering program focuses on practical training of mechanical systems, industrial equipment, and maintenance protocols for junior project engineers.",
        image: "/t&d/mechanical/2.jpeg",
      },
      {
        title: "What the Institution Provides",
        list: [
          "Engineering Mathematics core",
          "Engineering Mechanics principles",
          "Thermodynamics fundamentals",
          "Fluid Mechanics concepts",
          "Manufacturing Processes training",
          "Machine Design techniques",
          "Control Systems operations",
          "Piping system fundamentals",
          "Project management with theoretical and practical applications"
        ],
        image: "/t&d/provide.jpeg",
      },
      {
        title: "What Industry Wants",
        list: [
          "Ability to read and understand fabrication drawings",
          "Skill to interpret piping spool isometrics & supports",
          "Competence in managing erection work with quality & safety",
          "Capability in project planning & scheduling",
          "Proficiency in preparing fabrication drawings",
          "Understanding of piping layouts & specifications"
        ],
        image: "/t&d/wants.jpeg",
      },
      {
        title: "What I3D Provides",
        list: [
          "Drawing Reading & Interpretation",
          "Fabrication Process & Reporting",
          "Piping Spool, Isometric & Supporting",
          "Welding & Fitting techniques",
          "Equipment Manufacturing & Erection",
          "Project Planning & Scheduling",
          "Costing & Digitalization methods",
          "Safety & Quality standards"
        ],
        image: "/jpe/mechanical_section4.jpg",
      }
    ]
  }
];


const JPEPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);

  return (
    <div className="jpe-page" style={{ paddingTop: "80px" }}>
      <HeroSection>
        <Container>
          <div className="text-center mb-4">
            <h1 className="display-4 fw-bold mb-3 text-gradient">
              Junior Project Engineer Program
            </h1>
            <p className="lead mb-4" style={{ maxWidth: "700px", margin: "0 auto" }}>
              Transform into an industry-ready professional through our specialized, hands-on training programs
            </p>
            <div className="d-flex flex-wrap justify-content-center gap-3 mt-4">
              {categories.map((cat, idx) => (
                <Button
                  key={idx}
                  variant={selectedCategory?.name === cat.name ? "primary" : "outline-primary"}
                  className="rounded-pill px-4 py-2"
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat.name} Engineering
                </Button>
              ))}
            </div>
          </div>
        </Container>
      </HeroSection>

      <Container className="py-5">
        {/* Category Cards */}
        <Row className="g-4 mb-5">
          {categories.map((category, index) => (
            <Col xl={4} lg={6} md={6} key={index}>
              <CategoryCard
                className={selectedCategory?.name === category.name ? "selected" : ""}
                onClick={() => setSelectedCategory(category)}
              >
                <CardImageContainer>
                  <CardImage
                    src={category.image}
                    alt={category.name}
                  />
                  {selectedCategory?.name === category.name && (
                    <SelectedBadge>
                      <FiCheckCircle size={24} />
                    </SelectedBadge>
                  )}
                </CardImageContainer>
                <Card.Body className="py-4">
                  <Card.Title className="h4 mb-3">
                    {category.name} Engineering
                  </Card.Title>
                  <Card.Text className="text-muted mb-4">
                    {category.sections[0].content}
                  </Card.Text>
                  <Button 
                    variant="link" 
                    className="text-primary p-0 d-flex align-items-center"
                  >
                    Explore Program <FiArrowUpRight className="ms-2" />
                  </Button>
                </Card.Body>
              </CategoryCard>
            </Col>
          ))}
        </Row>

        {/* Category Details */}
        {selectedCategory && (
          <div className="category-details">
            <h2 className="text-center mb-5 fw-bold">
              {selectedCategory.name} Engineering Program Details
            </h2>
            
            {selectedCategory.sections.map((section, idx) => (
              <DetailBlock key={idx}>
                <Row className="align-items-center g-4">
                  <Col lg={5} className={idx % 2 === 0 ? "order-lg-1" : "order-lg-2"}>
                    <SectionImage
                      src={section.image}
                      alt={section.title}
                    />
                  </Col>
                  <Col lg={7} className={idx % 2 === 0 ? "order-lg-2" : "order-lg-1"}>
                    <h3 className="h3 fw-bold mb-4">
                      {section.title}
                    </h3>
                    {section.content && (
                      <p className="lead mb-4">{section.content}</p>
                    )}
                    {section.list && (
                      <StyledList>
                        {section.list.map((item, i) => (
                          <li key={i}>
                            <span className="bullet-icon">▹</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </StyledList>
                    )}
                  </Col>
                </Row>
              </DetailBlock>
            ))}
          </div>
        )}
      </Container>

      <Footer />
    </div>
  );
};

export default JPEPage;